// What every page of the public site shares: the company's settings, the
// interface language (English / Hindi, kept in the browser) and the theme
// studio's live preview.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TRANSLATIONS } from './translations.js';
import { applyTheme } from './theme.js';

const Ctx = createContext(null);
const LANG_KEY = 'kg_lang';

export function SiteProvider({ boot, children }) {
  const [settings, setSettings] = useState(boot.settings || {});
  const [lang, setLangState] = useState('en'); // the server always draws English; a saved choice applies once the page is live

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(LANG_KEY);
      if (saved && TRANSLATIONS[saved]) setLangState(saved);
    } catch { /* private browsing: stay on English */ }
  }, []);
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const setLang = useCallback((next) => {
    if (!TRANSLATIONS[next]) return;
    setLangState(next);
    try { window.localStorage.setItem(LANG_KEY, next); } catch { /* ignore */ }
  }, []);

  // The theme studio shows this site in a frame and sends it looks to try
  // (?theme_preview=1). Nothing is saved; links keep the preview going.
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has('theme_preview')) return undefined;
    const onMessage = (e) => {
      if (e.origin !== window.location.origin || !e.data || e.data.type !== 'kg:theme-preview') return;
      setSettings((s) => ({ ...s, ...e.data.settings }));
      applyTheme({ ...settings, ...e.data.settings });
    };
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a || a.target === '_blank') return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.searchParams.has('theme_preview')) return;
      url.searchParams.set('theme_preview', '1');
      a.href = url.toString();
    };
    window.addEventListener('message', onMessage);
    document.addEventListener('click', onClick, true);
    if (window.parent !== window) window.parent.postMessage({ type: 'kg:theme-preview-ready', path: window.location.pathname }, window.location.origin);
    return () => { window.removeEventListener('message', onMessage); document.removeEventListener('click', onClick, true); };
  }, [settings]);

  const t = useCallback((key, fallback) => TRANSLATIONS[lang]?.[key] ?? TRANSLATIONS.en[key] ?? fallback ?? key, [lang]);
  const value = useMemo(() => ({ ...boot, settings, lang, setLang, t }), [boot, settings, lang, setLang, t]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useSite = () => useContext(Ctx);
export const useT = () => useContext(Ctx).t;
